import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuFolderPlus, LuPencil } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import SectionedFormShell from '../../../components/form/SectionedFormShell';
import SearchSelect from '../../../components/form/SearchSelect';
import { Field, SelectInput, TextArea, TextInput, ToggleTile } from '../../../components/form/FormControls';
import { db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { PROJECT_DESIGN } from '../../../utils/projectDesign';
import { createProject, fetchCustomerPaymentPeriod, updateProject } from '../projectActions';
import { isCurrencyFixedFlag } from '../projectColumns';
import {
  CENTER_TYPES,
  DEPARTMENTS,
  DESIGNS,
  EMPTY_PROJECT_FORM,
  FIELD_SECTIONS,
  FORM_PHASES,
  PROJECT_FORM_SECTIONS,
} from './projectFormOptions';

const FORM_ID = 'project-form';
const fieldId = (name) => `project-form-${name}`;

// Proje kaydından form değerleri (liste verisi metne çevrilmiş olabilir)
const toForm = (project) => (project ? Object.fromEntries(Object.keys(EMPTY_PROJECT_FORM).map((key) => {
  if (key === 'isCurrencyFixed') return [key, isCurrencyFixedFlag(project)];
  return [key, project[key] || ''];
})) : EMPTY_PROJECT_FORM);

const byLabel = (locale) => (a, b) => a.label.localeCompare(b.label, locale);

/** Customers, centres and external staff (coordinators) for the selects. */
const useProjectFormLists = () => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [lists, setLists] = useState({ customers: [], centers: [], coordinators: [] });

  useEffect(() => {
    let cancelled = false;
    const load = async (collectionName, toOption, errorKey, key, locale) => {
      try {
        const snapshot = await getDocs(collection(db, collectionName));
        const options = snapshot.docs.map((d) => toOption(d.data())).filter((item) => item.label).sort(byLabel(locale));
        if (!cancelled) setLists((prev) => ({ ...prev, [key]: options }));
      } catch (error) {
        console.error(`Error fetching ${collectionName}:`, error);
        toast.error(tRef.current(`projectManagement.form.toast.${errorKey}`));
      }
    };
    load('Customers', (data) => ({ value: data['Başlık'], label: data['Başlık'] }), 'customersError', 'customers');
    load('Centers', (data) => ({ value: data['Adı'], label: data['Adı'] }), 'centersError', 'centers');
    load('ExternalPersonnels', (data) => {
      const fullName = `${data['Adı'] || ''} ${data['Soyadı'] || ''}`.trim();
      return { value: fullName, label: fullName };
    }, 'personnelError', 'coordinators', 'tr');
    return () => {
      cancelled = true;
    };
  }, []);

  return lists;
};

/**
 * "Yeni Proje Ekle" (no `project`) and "Proje Düzenle": every project field
 * in four sections (genel, çalışma, onaylar, finans). Choosing a customer
 * fills the payment frequency from the customer card. Adding a project also
 * opens "Yeni Proje" tasks for the project team (as before).
 */
const ProjectFormModal = ({ project, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(project);
  const lists = useProjectFormLists();
  const initial = useMemo(() => toForm(project), [project]);
  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('general');
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(formData) !== JSON.stringify(initial);

  const sections = PROJECT_FORM_SECTIONS.map((section) => ({
    ...section,
    label: t(`projectManagement.form.sections.${section.id}`),
    hint: t(`projectManagement.form.sections.${section.id}Hint`),
  }));
  const translate = (options) => options.map((option) => ({ value: option.value, label: t(option.labelKey) }));

  const setValue = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleChange = (event) => setValue(event.target.name, event.target.value);

  // Müşteri seçilince ödeme sıklığı müşteri kartındaki "Ödeme Periyodu"ndan
  // dolar (eklemede boşsa boşalır, düzenlemede mevcut değer korunur)
  const handleCustomerChange = async (customer) => {
    setValue('customer', customer);
    if (!customer) return;
    try {
      const period = await fetchCustomerPaymentPeriod(customer);
      // Müşteri kaydı bulunamazsa ödeme sıklığına dokunulmaz
      if (period === undefined) return;
      setFormData((prev) => ({
        ...prev,
        customer,
        paymentFrequency: isEdit ? (period || prev.paymentFrequency) : (period || ''),
      }));
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error(t('projectManagement.form.toast.customerInfoError'));
    }
  };

  const validate = () => {
    const found = {};
    if (!formData.customer) found.customer = t('dataTable.form.required');
    if (!formData.titleShort) found.titleShort = t('dataTable.form.required');
    if (formData.isCurrencyFixed) {
      const rateNum = parseFloat(formData.fixedCurrencyRate);
      if (!rateNum || rateNum <= 0) found.fixedCurrencyRate = t('projectManagement.form.errors.rate');
    }
    return found;
  };

  const sectionErrors = {};
  Object.keys(errors).forEach((field) => {
    if (!errors[field]) return;
    const section = FIELD_SECTIONS[field] || 'general';
    sectionErrors[section] = (sectionErrors[section] || 0) + 1;
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(found.fixedCurrencyRate && !found.customer && !found.titleShort
        ? t('projectManagement.form.errors.rate')
        : t('projectManagement.form.errors.required'));
      const firstSection = PROJECT_FORM_SECTIONS.find((section) => Object.keys(found).some((field) => (FIELD_SECTIONS[field] || 'general') === section.id));
      if (firstSection) setActiveSection(firstSection.id);
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateProject(project.id, formData);
        toast.success(t('projectManagement.form.toast.updated'));
      } else {
        const taskCount = await createProject(formData);
        toast.success(t('projectManagement.form.toast.added', { count: taskCount }));
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error(isEdit ? 'Error updating project:' : 'Error adding project:', error);
      toast.error(t(isEdit ? 'projectManagement.form.toast.updateError' : 'projectManagement.form.toast.addError'));
      setSaving(false);
    }
  };

  const text = (name, props = {}) => (
    <TextInput id={fieldId(name)} name={name} value={formData[name]} onChange={handleChange} error={errors[name]} autoComplete="off" {...props} />
  );
  const label = (name) => t(`projectManagement.form.fields.${name}`);
  const hybridHint = formData.projectDesign === PROJECT_DESIGN.HYBRID
    ? `${t('projectManagement.form.hints.hybrid')}${isEdit ? ` ${t('projectManagement.form.hints.hybridEdit')}` : ''}`
    : undefined;

  let content;
  if (activeSection === 'general') {
    content = (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('customer')} htmlFor={fieldId('customer')} required error={errors.customer} className="sm:tw-col-span-2">
          <SearchSelect
            inputId={fieldId('customer')}
            options={lists.customers}
            value={formData.customer}
            onChange={handleCustomerChange}
            placeholder={t('projectManagement.form.placeholders.customer')}
            error={errors.customer}
          />
        </Field>
        <Field label={label('titleShort')} htmlFor={fieldId('titleShort')} required error={errors.titleShort}>
          {text('titleShort', { placeholder: t('projectManagement.form.placeholders.titleShort') })}
        </Field>
        <Field label={label('protocolNumber')} htmlFor={fieldId('protocolNumber')}>
          {text('protocolNumber')}
        </Field>
        <Field label={label('titleLong')} htmlFor={fieldId('titleLong')} className="sm:tw-col-span-2">
          <TextArea id={fieldId('titleLong')} name="titleLong" rows={3} value={formData.titleLong} onChange={handleChange} placeholder={t('projectManagement.form.placeholders.titleLong')} />
        </Field>
        <Field label={label('coordinatorAcademician')} htmlFor={fieldId('coordinatorAcademician')} className="sm:tw-col-span-2">
          {/* A coordinator missing from the list (old free text) stays as "(liste dışı)" */}
          <SearchSelect
            inputId={fieldId('coordinatorAcademician')}
            options={lists.coordinators}
            value={formData.coordinatorAcademician}
            onChange={(value) => setValue('coordinatorAcademician', value)}
            placeholder={t('projectManagement.form.placeholders.coordinator')}
            isClearable
          />
        </Field>
      </div>
    );
  } else if (activeSection === 'study') {
    content = (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('projectDepartment')} htmlFor={fieldId('projectDepartment')}>
          <SearchSelect
            inputId={fieldId('projectDepartment')}
            options={DEPARTMENTS}
            value={formData.projectDepartment}
            onChange={(value) => setValue('projectDepartment', value)}
            placeholder={t('projectManagement.form.placeholders.department')}
          />
        </Field>
        <Field label={label('projectPhase')} htmlFor={fieldId('projectPhase')}>
          <SelectInput
            id={fieldId('projectPhase')}
            name="projectPhase"
            value={formData.projectPhase}
            current={formData.projectPhase}
            onChange={handleChange}
            options={translate(FORM_PHASES)}
            placeholder={t('projectManagement.form.placeholders.phase')}
          />
        </Field>
        <Field label={label('projectDesign')} htmlFor={fieldId('projectDesign')} hint={hybridHint} className="sm:tw-col-span-2">
          <SelectInput
            id={fieldId('projectDesign')}
            name="projectDesign"
            value={formData.projectDesign}
            current={formData.projectDesign}
            onChange={handleChange}
            options={translate(DESIGNS)}
            placeholder={t('projectManagement.form.placeholders.design')}
          />
        </Field>
        <Field label={label('projectCentersType')} htmlFor={fieldId('projectCentersType')}>
          <SelectInput
            id={fieldId('projectCentersType')}
            name="projectCentersType"
            value={formData.projectCentersType}
            current={formData.projectCentersType}
            onChange={handleChange}
            options={translate(CENTER_TYPES)}
            placeholder={t('projectManagement.form.placeholders.centerType')}
          />
        </Field>
        <Field label={label('center')} htmlFor={fieldId('center')}>
          <SearchSelect
            inputId={fieldId('center')}
            options={lists.centers}
            value={formData.center}
            onChange={(value) => setValue('center', value)}
            placeholder={t('projectManagement.form.placeholders.center')}
          />
        </Field>
        <Field label={label('projectDate')} htmlFor={fieldId('projectDate')}>
          {text('projectDate', { type: 'date' })}
        </Field>
        <Field label={label('projectDueDate')} htmlFor={fieldId('projectDueDate')}>
          {text('projectDueDate', { type: 'date' })}
        </Field>
      </div>
    );
  } else if (activeSection === 'approvals') {
    content = (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('ethicsCommitteeDate')} htmlFor={fieldId('ethicsCommitteeDate')}>
          {text('ethicsCommitteeDate', { type: 'date' })}
        </Field>
        <Field label={label('ethicsCommitteeNumber')} htmlFor={fieldId('ethicsCommitteeNumber')}>
          {text('ethicsCommitteeNumber')}
        </Field>
        <Field label={label('ethicsCommitteePlace')} htmlFor={fieldId('ethicsCommitteePlace')} className="sm:tw-col-span-2">
          {text('ethicsCommitteePlace')}
        </Field>
        <Field label={label('sbFirstApprovalDate')} htmlFor={fieldId('sbFirstApprovalDate')}>
          {text('sbFirstApprovalDate', { type: 'date' })}
        </Field>
        <Field label={label('sbFirstApprovalNumber')} htmlFor={fieldId('sbFirstApprovalNumber')}>
          {text('sbFirstApprovalNumber')}
        </Field>
        <Field label={label('sbProjectNumber')} htmlFor={fieldId('sbProjectNumber')}>
          {text('sbProjectNumber')}
        </Field>
      </div>
    );
  } else {
    content = (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('firstInvoiceDate')} htmlFor={fieldId('firstInvoiceDate')}>
          {text('firstInvoiceDate', { type: 'date' })}
        </Field>
        <Field label={label('paymentFrequency')} htmlFor={fieldId('paymentFrequency')} hint={t('projectManagement.form.hints.paymentFrequency')}>
          {text('paymentFrequency')}
        </Field>
        <Field label={label('isCurrencyFixed')} htmlFor={fieldId('isCurrencyFixed')}>
          <ToggleTile
            id={fieldId('isCurrencyFixed')}
            label={formData.isCurrencyFixed ? t('projectManagement.yes') : t('projectManagement.no')}
            checked={Boolean(formData.isCurrencyFixed)}
            onChange={(checked) => setValue('isCurrencyFixed', checked)}
          />
        </Field>
        {formData.isCurrencyFixed && (
          <Field label={label('fixedCurrencyRate')} htmlFor={fieldId('fixedCurrencyRate')} required error={errors.fixedCurrencyRate}>
            {text('fixedCurrencyRate', { type: 'number', step: '0.0001', min: '0', inputMode: 'decimal', placeholder: t('projectManagement.form.placeholders.rate') })}
          </Field>
        )}
      </div>
    );
  }

  return (
    <SectionedFormShell
      title={t(isEdit ? 'projectManagement.form.editTitle' : 'projectManagement.form.addTitle')}
      subtitle={isEdit ? initial.titleShort || initial.customer : t('projectManagement.form.addSubtitle')}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="xs">
          {isEdit ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuFolderPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      formId={FORM_ID}
      sections={sections}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      sectionErrors={sectionErrors}
      hasErrors={Object.values(errors).some(Boolean)}
      onSubmit={handleSubmit}
      saving={saving}
      submitLabel={t(isEdit ? 'projectManagement.form.submitEdit' : 'projectManagement.form.submitAdd')}
      dirty={dirty}
      onClose={onClose}
    >
      {content}
    </SectionedFormShell>
  );
};

ProjectFormModal.propTypes = {
  project: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ProjectFormModal;
